import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useSelectedPage } from "../../../../Hooks/Display/usePages";
import { useSelectedComponent } from "../../../../Hooks/Display/useComponents";
import { useMemo, useState } from "react";
import AttributeTypes from "../../../Attributes/AttributeTypes";
import { useTranslation } from "react-i18next";
import { getGroupedData } from "../../../Attributes/attributeGroupUtils";

function SelectAttributesDialog({ attributes, componentAttributes, onSelect, onCancel }) {

    const { t } = useTranslation([
        "attribute/attribute_type",
        "display/tables/attribute"
    ], { useSuspense: false })

    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const dialogTitle = useMemo(() => {
        return selectedComponent.componentType === "Calendar"
            ? t("select_attributes_dialog.calendar_component_title", { ns: "display/tables/attribute" })
            : t("select_attributes_dialog.title", { ns: "display/tables/attribute" })
    }, [selectedPage, selectedComponent]);

    const [selectedAttributes, setSelectedAttributes] = useState(componentAttributes);
    const [expandedGroups, setExpandedGroups] = useState({});

    const hasGroups = attributes?.some(attr => attr.group && attr.group.trim());

    function getDisplayOrder() {
        if (!hasGroups) {
            return attributes.map(a => a._id);
        }
        const grouped = getGroupedData(attributes);
        if (!grouped.data) return attributes.map(a => a._id);
        const orderedIds = [];
        grouped.data.forEach(group => {
            (group.items || []).forEach(item => {
                orderedIds.push(item._id);
            });
        });
        return orderedIds;
    }

    function insertAtDisplayPosition(currentSelected, newAttributeData) {
        const displayOrder = getDisplayOrder();
        const newId = newAttributeData.attributeId;
        const newIndexInDisplay = displayOrder.indexOf(newId);

        let insertIndex = currentSelected.length;
        for (let i = 0; i < currentSelected.length; i++) {
            const selectedIndexInDisplay = displayOrder.indexOf(currentSelected[i].attributeId);
            if (selectedIndexInDisplay > newIndexInDisplay) {
                insertIndex = i;
                break;
            }
        }

        const result = [...currentSelected];
        result.splice(insertIndex, 0, newAttributeData);
        return result;
    }

    function groupHeaderCellRender(defaultRendering, props) {
        if (props.rowType !== "groupHeader") {
            return defaultRendering;
        }
        const dataItem = props.dataItem;
        const groupName = dataItem.value || "(Ungrouped)";
        const count = dataItem.items ? dataItem.items.length : 0;
        const isExpanded = dataItem.expanded !== false;
        return (
            <td
                key={props.id}
                colSpan={props.columnsCount}
                style={props.style}
                className="k-group-cell"
                role="gridcell"
            >
                <p className="k-reset d-flex align-items-center">
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            setExpandedGroups(prev => ({ ...prev, [groupName]: !isExpanded }));
                            props.onChange?.({
                                dataItem: dataItem,
                                dataIndex: props.dataIndex,
                                syntheticEvent: e,
                                field: "expanded",
                                value: !isExpanded
                            });
                        }}
                        className={isExpanded ? "k-i-collapse k-icon me-2" : "k-i-expand k-icon me-2"}
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                    />
                    <span className="fw-bold me-2">{groupName}</span>
                    <span className="badge bg-light text-dark">{count}</span>
                </p>
            </td>
        );
    }

    function isAttributeSelected(id) {
        return selectedAttributes.some(attribute => attribute.attributeId === id);
    }

    function selectAttribute(id) {
        const attribute = attributes.find(attr => attr._id === id);
        const newAttributeData = {
            attributeId: id,
            hyperlink: false,
            group: attribute?.group || "",
            tooltip: false,
            cellRules: false,
            targetPageId: ""
        };
        setSelectedAttributes(insertAtDisplayPosition(selectedAttributes, newAttributeData));
    }

    function deselectAttribute(id) {
        setSelectedAttributes(selectedAttributes.filter(attribute => attribute.attributeId !== id));
    }

    function handleCheck(event, attributeId) {
        if (event.target.checked) {
            selectAttribute(attributeId);
        } else {
            deselectAttribute(attributeId);
        }
    }

    function handleSelect() {
        onSelect(selectedAttributes);
    }

    function handleSelectAll() {
        if (selectedAttributes.length === attributes.length) {
            setSelectedAttributes([]);
        } else {
            const displayOrder = getDisplayOrder();
            const attributeMap = Object.fromEntries(attributes.map(a => [a._id, a]));
            setSelectedAttributes(displayOrder.map(id => {
                const attribute = attributeMap[id];
                return {
                    attributeId: attribute._id,
                    hyperlink: false,
                    group: attribute.group || "",
                    tooltip: false,
                    cellRules: false,
                    targetPageId: ""
                };
            }));
        }
    }

    function getDisplayType(type) {
        const translatedTypes = AttributeTypes.getTranslatedTypes(t);
        const typeObj = translatedTypes.find(t => t.value === type);
        if (!typeObj && type === "Entity Lookup") {
            return t("attribute_type_label.entity_lookup", { ns: "attribute/attribute_type" });
        }
        return typeObj ? typeObj.text : "Type not found";
    }

    return (
        <Dialog
            title={dialogTitle}
            height={500}
            width={"70%"}
            onClose={onCancel}
        >
            <div className="m-3">
                <Grid
                    data={hasGroups ? (() => {
                        const grouped = getGroupedData(attributes);
                        if (grouped.data) {
                            grouped.data.forEach(group => {
                                const groupName = group.value || "(Ungrouped)";
                                group.expanded = expandedGroups.hasOwnProperty(groupName) ? expandedGroups[groupName] : true;
                            });
                        }
                        return grouped;
                    })() : attributes}
                    group={hasGroups ? [{ field: "group" }] : undefined}
                    expandField={hasGroups ? "expanded" : undefined}
                    groupable={false}
                    scrollable={false}
                    dataItemKey="_id"
                    cellRender={hasGroups ? groupHeaderCellRender : undefined}
                >
                    <GridColumn
                        field="attributeLabel"
                        title={t("select_attributes_dialog.grid.column_name", { ns: "display/tables/attribute" })}
                        cell={({ dataItem, rowType }) => (
                            hasGroups && rowType === "groupHeader" ? null : (
                                <td>{dataItem.attributeLabel}</td>
                            )
                        )}
                    />
                    <GridColumn
                        title="Type"
                        cell={({ dataItem, rowType }) => (
                            hasGroups && rowType === "groupHeader" ? null : (
                                <td>
                                    {getDisplayType(dataItem.attributeType)}
                                </td>
                            )
                        )}
                    />
                    <GridColumn
                        field="" title="Select"
                        headerCell={() => (
                            (selectedComponent.componentType === "Calendar" ?
                                null :
                                <Checkbox
                                    onClick={handleSelectAll}
                                    label={t("select_attributes_dialog.grid.select_all", { ns: "display/tables/attribute" })}
                                    labelPlacement="after"
                                    className="checkbox"
                                    disabled={selectedComponent.componentType === "Calendar" && selectedAttributes.length >= 4}
                                    checked={selectedAttributes.length === attributes.length}
                                    indeterminate={selectedAttributes.length > 0 && selectedAttributes.length < attributes.length}
                                />
                            )
                        )}
                        cell={({ dataItem, rowType }) => (
                            hasGroups && rowType === "groupHeader" ? null : (
                            <td>
                                <Checkbox
                                    onClick={(event) => handleCheck(event, dataItem._id)}
                                    disabled={
                                        (selectedPage.subPageType === "New Record" && dataItem.required === true) ||
                                        (
                                            selectedComponent.componentType === "Calendar" &&
                                            selectedAttributes.length >= 4 &&
                                            !isAttributeSelected(dataItem._id)
                                        )
                                    }
                                    checked={
                                        selectedComponent.componentType === "Form" && selectedPage.subPageType === "New Record"
                                            ? dataItem.required === true || isAttributeSelected(dataItem._id)
                                            : isAttributeSelected(dataItem._id)
                                    }
                                />
                            </td>
                            )
                        )}
                    />
                </Grid>
            </div>
            <DialogActionsBar layout="end">
                <div className="m-2 me-4 mb-3">
                    {
                        selectedAttributes.length === 0 &&
                        <span className="error">
                            {t("select_attributes_dialog.errors.select_attributes", { ns: "display/tables/attribute" })}
                        </span>
                    }
                    <button className="btn" onClick={onCancel}>
                        {t("select_attributes_dialog.buttons.cancel", { ns: "display/tables/attribute" })}
                    </button>
                    <button className="btn btn-primary" onClick={handleSelect} disabled={selectedAttributes.length === 0}>
                        {t("select_attributes_dialog.buttons.select", { ns: "display/tables/attribute" })}
                    </button>
                </div>
            </DialogActionsBar>
        </Dialog>
    )
}

export default SelectAttributesDialog;